import axios, { AxiosInstance, AxiosError } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors — only 401 triggers logout/redirect, not 403
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = (error.config ?? {}) as AxiosError['config'] & { __retryCount?: number };
    const method = (config?.method ?? 'get').toLowerCase();
    const status = error.response?.status;
    const isNetworkError = !error.response;
    const isTransientServerError = status != null && status >= 500;
    const isRetryableMethod = method === 'get';
    const retryCount = config.__retryCount ?? 0;
    const maxRetries = 2;

    // Lightweight retry for transient public read failures to reduce blank-page states.
    if (isRetryableMethod && (isNetworkError || isTransientServerError) && retryCount < maxRetries) {
      config.__retryCount = retryCount + 1;
      const backoffMs = 250 * Math.pow(2, retryCount);
      await new Promise((resolve) => setTimeout(resolve, backoffMs));
      return api.request(config);
    }

    const reqPath = error.config?.url ?? '';
    const isAdminApi = reqPath.includes('/admin/');
    // 401 (Unauthorized) means missing/expired session → log out and send to login.
    // 403 (Forbidden) means a logged-in user lacks permission — do NOT log them out.
    if (status === 401 && isAdminApi) {
      localStorage.removeItem('token');
      if (window.location.pathname.startsWith('/admin') && !window.location.pathname.startsWith('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Public API (no auth required)
export const publicApi = {
  events: {
    getUpcoming: (limit = 10) => api.get(`/events?limit=${limit}`),
    getByVenue: (venueId: number, limit = 10) => api.get(`/events/venue/${venueId}?limit=${limit}`),
    search: (params: {
      search?: string;
      venueId?: number;
      /** Category term slug (matches upstream `category` query param). */
      category?: string;
      from?: string;
      to?: string;
      limit?: number;
      offset?: number;
      includePast?: boolean;
      prioritizeFeatured?: boolean;
      includeAllToday?: boolean;
    }) => api.get('/events', { params }),
    /** Distinct TEC (`tribe_events_cat`) terms for filter dropdowns (only categories that have upcoming events). */
    getCategoryFilterOptions: () => api.get('/events/categories'),
    /** Venues that have at least one upcoming event in the listing window. */
    getFilterLocations: () => api.get('/events/filter/locations'),
    getBySlug: (slug: string, start?: string) =>
      api.get(`/events/${encodeURIComponent(slug)}`, { params: start ? { start } : {} }),
    getSeriesBySlug: (slug: string) => api.get(`/events/series/${encodeURIComponent(slug)}`),
  },
  venues: {
    getAll: () => api.get('/venues'),
  },
  posts: {
    getAll: (params?: { category?: string }) =>
      api.get('/posts', { params: params?.category ? { category: params.category } : {} }),
    getCategories: () => api.get('/posts/categories'),
    getBySlug: (slug: string) => api.get(`/posts/${encodeURIComponent(slug)}`),
  },
  galleries: {
    getAll: () => api.get('/galleries'),
    getBySlug: (slug: string) => api.get(`/galleries/${encodeURIComponent(slug)}`),
  },
  pages: {
    getAll: () => api.get('/pages'),
    getBySlug: (slug: string) => api.get(`/pages/${encodeURIComponent(slug)}`),
    getNav: () => api.get('/nav'),
  },
  leads: {
    getActive: () => api.get('/leads'),
  },
  nav: {
    getAll: () => api.get('/navigation'),
  },
  footerNav: {
    getAll: () => api.get('/footer-nav'),
  },
  schedule: {
    getNowPlaying: () => api.get('/schedule/now'),
    getLineup: () => api.get('/schedule/lineup'),
    getAll: () => api.get('/schedule'),
    getByDay: (day: number) => api.get(`/schedule/${day}`),
  },
  // Keep radio alias for backward compatibility
  radio: {
    getNowPlaying: () => api.get('/schedule/now'),
    getLineup: () => api.get('/schedule/lineup'),
    getSchedule: () => api.get('/schedule'),
    getScheduleByDay: (day: number) => api.get(`/schedule/${day}`),
  },
  settings: {
    get: () => api.get('/settings'),
  },
  businesses: {
    getAll: () => api.get('/businesses'),
    getBySlug: (slug: string) => api.get(`/businesses/slug/${encodeURIComponent(slug)}`),
    getReviewsBySlug: (slug: string) =>
      api.get(`/businesses/slug/${encodeURIComponent(slug)}/reviews`),
    getFeatured: () => api.get('/businesses/featured'),
    getOnAir: () => api.get('/businesses/on-air'),
    getFoundingMembers: () => api.get('/businesses/founding-members'),
    getByCategory: (category: string) => api.get(`/businesses/category/${category}`),
    getCategories: () => api.get('/businesses/categories'),
    getCategoryNames: () => api.get('/businesses/categories/names'),
    getCategoryBySlug: (slug: string) => api.get(`/businesses/categories/slug/${slug}`),
    getCategoryByName: (name: string) => api.get(`/businesses/categories/name/${name}`),
  },
  podcast: {
    getEpisodes: (season?: number) =>
      api.get('/podcast/episodes', {
        params: season != null ? { season } : {},
        timeout: 20000,
      }),
  },
  news: {
    getLatest: (limit = 6) => api.get('/pages/news-feed', { params: { limit } }),
  },
  radioSchedule: {
    getUpcoming: (limit = 6) => api.get('/radio-schedule/upcoming', { params: { limit } }),
    getByDate: (date: string) => api.get(`/radio-schedule/date/${date}`),
    getToday: () => api.get('/radio-schedule/today'),
    getRange: (start: string, end: string) => api.get('/radio-schedule', { params: { start, end } }),
  },
  tvGuide: {
    getByDate: (date: string) => api.get(`/tv-guide/date/${date}`),
    getToday: () => api.get('/tv-guide/today'),
  },
  contests: {
    getAll: () => api.get('/contests'),
    getActive: () => api.get('/contests/active'),
    getById: (id: number) => api.get(`/contests/${id}`),
    getBySlug: (slug: string) => api.get(`/contests/${slug}`),
    list: () => api.get('/contests'),
    home: () => api.get('/contests/home'),
  },
  forecasts: {
    getFeatured: () => api.get('/public/forecasts/featured'),
    featured: () => api.get('/public/forecasts/featured'),
  },
  directory: {
    submitInquiry: (body: {
      businessSlug: string;
      visitorName: string;
      visitorEmail: string;
      visitorPhone?: string;
      message: string;
    }) => api.post('/directory/inquiries', body),
    submitClaim: (body: {
      businessSlug: string;
      requesterName: string;
      requesterEmail: string;
      message?: string;
    }) => api.post('/directory/claim-requests', body),
    submitReview: (body: Record<string, unknown>) => api.post('/directory/reviews', body),
    submitListingRequest: (body: {
      businessName: string;
      description: string;
      category?: string;
      contactName: string;
      contactEmail: string;
      phone?: string;
      websiteUrl?: string;
      address?: string;
      city?: string;
      state?: string;
      zip?: string;
      notes?: string;
    }) => api.post('/directory/listing-requests', body),
  },
  contact: {
    submit: (body: {
      subjectType: string;
      visitorName: string;
      visitorEmail: string;
      visitorPhone?: string;
      message: string;
      marketingSmsOptIn?: boolean;
      nonMarketingSmsOptIn?: boolean;
      extraJson?: string;
    }) => api.post('/contact', body),
  },
  newsletter: {
    subscribe: (body: {
      email: string;
      nickname: string;
      firstName?: string;
      lastName?: string;
      website?: string;
      bio?: string;
      pronouns?: string;
      genderIdentity: string;
      sexualOrientation?: string;
      relationshipStatus: string;
      occupation?: string;
      birthDate?: string;
      favoriteLocalSpots?: string;
      hobbies: string[];
      phone?: string;
      newsletterOptIn: boolean;
      smsMarketingOptIn: boolean;
      smsTransactionalOptIn: boolean;
    }) => api.post('/newsletter/subscribe', body),
    unsubscribe: (email: string) => api.post('/newsletter/unsubscribe', { email }),
  },
};

// Admin API (auth required)
export const adminApi = {
  auth: {
    login: (username: string, password: string) =>
      api.post('/auth/login', { username, password }),
    changePassword: (username: string, newPassword: string) =>
      api.post('/auth/change-password', { username, newPassword }),
    ownerForgotPassword: (email: string) =>
      api.post('/auth/owner/forgot-password', { email }),
    ownerResetPassword: (token: string, password: string) =>
      api.post('/auth/owner/reset-password', { token, password }),
  },
  posts: {
    getAll: (params?: { search?: string; status?: string; page?: number; size?: number }) =>
      api.get('/admin/posts', { params }),
    create: (data: any) => api.post('/admin/posts', data),
    update: (id: number, data: any) => api.put(`/admin/posts/${id}`, data),
    delete: (id: number) => api.delete(`/admin/posts/${id}`),
  },
  media: {
    list: (params?: { type?: string; search?: string; page?: number; size?: number }) =>
      api.get('/admin/media', { params }),
    upload: (file: File, type = 'general', onProgress?: (pct: number) => void) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      return api.post('/admin/media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        onUploadProgress: (e) =>
          onProgress?.(e.total ? Math.round((e.loaded * 100) / e.total) : 0),
      });
    },
    updateAlt: (id: number, altText: string) => api.patch(`/admin/media/${id}`, { altText }),
    delete: (id: number) => api.delete(`/admin/media/${id}`),
  },
  postCategories: {
    getAll: () => api.get('/admin/post-categories'),
    create: (data: Record<string, unknown>) => api.post('/admin/post-categories', data),
    update: (id: number, data: Record<string, unknown>) => api.put(`/admin/post-categories/${id}`, data),
    delete: (id: number) => api.delete(`/admin/post-categories/${id}`),
  },
  businessCategories: {
    getAll: () => api.get('/admin/business-categories'),
    create: (data: Record<string, unknown>) => api.post('/admin/business-categories', data),
    update: (id: number, data: Record<string, unknown>) => api.put(`/admin/business-categories/${id}`, data),
    delete: (id: number) => api.delete(`/admin/business-categories/${id}`),
  },
  galleries: {
    // Query string avoids a browser-cached 301 on the bare list URL.
    getAll: () => api.get('/admin/galleries', { params: { view: 'admin' }, timeout: 30000 }),
    get: (id: number) => api.get(`/admin/galleries/${id}`),
    create: (data: Record<string, unknown>) => api.post('/admin/galleries', data),
    update: (id: number, data: Record<string, unknown>) => api.put(`/admin/galleries/${id}`, data),
    delete: (id: number) => api.delete(`/admin/galleries/${id}`),
    reorder: (orderedIds: number[]) => api.put('/admin/galleries/reorder', { orderedIds }),
    addPhoto: (galleryId: number, body: { imageUrl: string; caption?: string; sortOrder?: number }) =>
      api.post(`/admin/galleries/${galleryId}/photos`, body),
    updatePhoto: (
      galleryId: number,
      photoId: number,
      body: { imageUrl?: string; caption?: string; sortOrder?: number }
    ) => api.put(`/admin/galleries/${galleryId}/photos/${photoId}`, body),
    deletePhoto: (galleryId: number, photoId: number) =>
      api.delete(`/admin/galleries/${galleryId}/photos/${photoId}`),
    reorderPhotos: (galleryId: number, orderedIds: number[]) =>
      api.put(`/admin/galleries/${galleryId}/photos/reorder`, { orderedIds }),
    checkDuplicates: (
      galleryId: number,
      items: { sha256: string; dhash: string | null }[]
    ) => api.post(`/admin/galleries/${galleryId}/duplicates`, { items }),
    uploadOne: async (
      galleryId: number,
      file: File,
      onProgress?: (progress: number) => void
    ) => {
      const formData = new FormData();
      formData.append('files', file);
      const result = await api.post(`/admin/galleries/${galleryId}/upload-batch`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          onProgress?.(progress);
        },
      });
      const row = Array.isArray(result.data) ? result.data[0] : result.data;
      return row as { error?: string | null; duplicate?: boolean; photoId?: number };
    },
  },
  pages: {
    getAll: (params?: { search?: string; page?: number; size?: number }) =>
      api.get('/admin/pages', { params }),
    create: (data: any) => api.post('/admin/pages', data),
    update: (id: number, data: any) => api.put(`/admin/pages/${id}`, data),
    delete: (id: number) => api.delete(`/admin/pages/${id}`),
  },
  leads: {
    getAll: (params?: { search?: string; page?: number; size?: number; archived?: boolean }) =>
      api.get('/admin/leads', { params }),
    create: (data: any) => api.post('/admin/leads', data),
    update: (id: number, data: any) => api.put(`/admin/leads/${id}`, data),
    delete: (id: number) => api.delete(`/admin/leads/${id}`),
    reorder: (orderedIds: number[]) => api.put('/admin/leads/reorder', { orderedIds }),
    archive: (id: number) => api.put(`/admin/leads/${id}/archive`),
    unarchive: (id: number) => api.put(`/admin/leads/${id}/unarchive`),
  },
  nav: {
    getAll: () => api.get('/admin/nav'),
    create: (data: any) => api.post('/admin/nav', data),
    update: (id: number, data: any) => api.put(`/admin/nav/${id}`, data),
    delete: (id: number) => api.delete(`/admin/nav/${id}`),
  },
  schedules: {
    getAll: (includeInactive = false) =>
      api.get('/admin/schedules', { params: { includeInactive } }),
    create: (data: any) => api.post('/admin/schedules', data),
    update: (id: number, data: any) => api.put(`/admin/schedules/${id}`, data),
    delete: (id: number) => api.delete(`/admin/schedules/${id}`),
  },
  // Keep radio alias for backward compatibility
  radio: {
    getAll: (includeInactive = false) =>
      api.get('/admin/schedules', { params: { includeInactive } }),
    create: (data: any) => api.post('/admin/schedules', data),
    update: (id: number, data: any) => api.put(`/admin/schedules/${id}`, data),
    delete: (id: number) => api.delete(`/admin/schedules/${id}`),
  },
  venues: {
    getAll: () => api.get('/admin/venues'),
  },
  settings: {
    get: () => api.get('/admin/settings'),
    update: (data: any) => api.put('/admin/settings', data),
  },
  users: {
    getAll: () => api.get('/admin/users'),
    create: (data: any) => api.post('/admin/users', data),
    update: (id: number, data: any) => api.put(`/admin/users/${id}`, data),
    delete: (id: number) => api.delete(`/admin/users/${id}`),
  },
  businesses: {
    getAll: (params?: { search?: string; page?: number; size?: number }) =>
      api.get('/admin/businesses', { params }),
    create: (data: any) => api.post('/admin/businesses', data),
    update: (id: number, data: any) => api.put(`/admin/businesses/${id}`, data),
    delete: (id: number) => api.delete(`/admin/businesses/${id}`),
    importCsv: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/admin/businesses/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    inferCategories: () => api.post('/admin/businesses/infer-categories'),
  },
  directoryAdmin: {
    inquiries: () => api.get('/admin/directory/inquiries'),
    markInquiryRead: (id: number) => api.put(`/admin/directory/inquiries/${id}/read`),
    claimRequests: () => api.get('/admin/directory/claim-requests'),
    approveClaim: (id: number) => api.post(`/admin/directory/claim-requests/${id}/approve`),
    rejectClaim: (id: number) => api.post(`/admin/directory/claim-requests/${id}/reject`),
    reviews: () => api.get('/admin/directory/reviews'),
    setReviewStatus: (id: number, status: string) =>
      api.put(`/admin/directory/reviews/${id}/status`, { status }),
    assign: (userId: number, businessId: number) =>
      api.post('/admin/directory/assignments', { userId, businessId }),
    removeAssignment: (userId: number, businessId: number) =>
      api.delete('/admin/directory/assignments', { data: { userId, businessId } }),
    listingRequests: () => api.get('/admin/directory/listing-requests'),
    approveListingRequest: (id: number) => api.post(`/admin/directory/listing-requests/${id}/approve`),
    rejectListingRequest: (id: number, adminNotes?: string) =>
      api.post(`/admin/directory/listing-requests/${id}/reject`, { adminNotes: adminNotes ?? '' }),
    backfillListingOwners: (sendWelcomeEmail = true) =>
      api.post('/admin/directory/listing-requests/backfill-owners', null, {
        params: { sendWelcomeEmail },
      }),
  },
  contactMessages: {
    list: (params?: { search?: string; read?: boolean; page?: number; size?: number }) =>
      api.get('/admin/messages', { params }),
    markRead: (id: number) => api.put(`/admin/contact-messages/${id}/read`),
    markReplied: (id: number, staffNote?: string) =>
      api.put(`/admin/contact-messages/${id}/replied`, staffNote ? { staffNote } : {}),
    delete: (id: number) => api.delete(`/admin/contact-messages/${id}`),
    restore: (id: number) => api.put(`/admin/contact-messages/${id}/restore`),
    sendReply: (id: number, replyBody: { subject: string; message: string; fromEmail?: string }) =>
      api.post(`/admin/contact-messages/${id}/reply`, replyBody),
  },
  trash: {
    getCounts: () => api.get('/admin/trash/counts'),
    list: (entityType: string) => api.get(`/admin/trash/${entityType}`),
    restore: (entityType: string, id: number) => api.post(`/admin/trash/${entityType}/${id}/restore`),
    deletePermanently: (entityType: string, id: number) => api.delete(`/admin/trash/${entityType}/${id}`),
    empty: (entityType: string) => api.post(`/admin/trash/${entityType}/empty`),
  },
  shows: {
    getAll: () => api.get('/admin/shows'),
    create: (data: any) => api.post('/admin/shows', data),
    update: (id: number, data: any) => api.put(`/admin/shows/${id}`, data),
    delete: (id: number) => api.delete(`/admin/shows/${id}`),
  },
  contests: {
    getAll: () => api.get('/admin/contests'),
    list: () => api.get('/admin/contests'),
    create: (data: any) => api.post('/admin/contests', data),
    update: (id: number, data: any) => api.put(`/admin/contests/${id}`, data),
    delete: (id: number) => api.delete(`/admin/contests/${id}`),
    draw: (id: number) => api.post(`/admin/contests/${id}/draw`),
  },
};

export const ownerApi = {
  business: {
    get: () => api.get('/owner/business'),
    update: (data: Record<string, unknown>) => api.put('/owner/business', data),
  },
};

// Member API (separate auth from admin)
const memberApiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add member auth token to requests
memberApiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('memberToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle member auth errors
memberApiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    if (status === 401) {
      localStorage.removeItem('memberToken');
      localStorage.removeItem('member-auth-storage');
      // Don't auto-redirect, let the component handle it
    }
    return Promise.reject(error);
  }
);

export const memberApi = {
  auth: {
    signup: (body: {
      email: string;
      password: string;
      nickname: string;
      firstName?: string;
      lastName?: string;
      website?: string;
      bio?: string;
      pronouns?: string;
      genderIdentity: string;
      sexualOrientation?: string;
      relationshipStatus: string;
      occupation?: string;
      birthDate?: string;
      favoriteLocalSpots?: string;
      hobbies: string[];
      phone?: string;
      newsletterOptIn: boolean;
      smsMarketingOptIn: boolean;
      smsTransactionalOptIn: boolean;
    }) => api.post('/members/signup', body),
    login: (email: string, password: string) =>
      api.post('/members/login', { email, password }),
    checkEmail: (email: string) => api.post('/members/check-email', { email }),
    forgotPassword: (email: string) => api.post('/members/forgot-password', { email }),
    resetPassword: (token: string, password: string) => api.post('/members/reset-password', { token, password }),
    changePassword: (currentPassword: string, newPassword: string) => 
      memberApiClient.post('/members/change-password', { currentPassword, newPassword }),
    consumeMagicLink: (token: string) => api.post('/members/magic-login', { token }),
  },
  profile: {
    get: () => memberApiClient.get('/members/profile'),
    update: (body: Record<string, unknown>) => memberApiClient.put('/members/profile', body),
  },
  events: {
    submit: (body: {
      title: string;
      description?: string;
      excerpt?: string;
      startDateTime: string;
      endDateTime?: string;
      allDay?: boolean;
      timezone?: string;
      existingVenueId?: number;
      venueName?: string;
      venueAddress?: string;
      venueCity?: string;
      venueState?: string;
      venueZip?: string;
      category?: string;
      eventWebsiteUrl?: string;
      imageUrl?: string;
    }) => memberApiClient.post('/event-submissions', body),
    getMySubmissions: () => memberApiClient.get('/event-submissions/my-submissions'),
    getMySubmission: (id: number) => memberApiClient.get(`/event-submissions/my-submissions/${id}`),
    getPendingCount: () => memberApiClient.get('/event-submissions/count-pending'),
    uploadImage: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return memberApiClient.post('/event-submissions/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    getCategories: () => api.get('/event-categories'),
  },
};

// Image Proxy Utility
export function getProxiedImageUrl(
  url: string | null | undefined,
  options?: { w?: number; h?: number }
): string | undefined {
  if (!url) return undefined;

  // Skip if already proxied or is data URI
  if (url.startsWith('data:') || url.includes('/api/v1/proxy/image')) {
    return url;
  }

  // Skip if relative URL (local image)
  if (url.startsWith('/') && !url.startsWith('//')) {
    return url;
  }

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();

    // Skip if already from our domain
    if (host === window.location.hostname.toLowerCase()) {
      return url;
    }

    const IMAGE_EXT_RE = /\.(jpe?g|png|gif|svg|webp|avif|bmp)(\?.*)?$/i;
    const isDirectMedia = url.includes('/wp-content/uploads/') || IMAGE_EXT_RE.test(url);

    // Skip proxy for known safe domains when URL is a direct media file
    if (isDirectMedia && (
      host === 'events.gayphx.com' ||
      host.endsWith('.gayphx.com')
    )) {
      return url;
    }

    // Build proxy URL
    const params = new URLSearchParams({ url });
    if (options?.w) params.set('w', String(options.w));
    if (options?.h) params.set('h', String(options.h));

    return `${API_URL}/proxy/image?${params.toString()}`;
  } catch {
    // If URL parsing fails, return original
    return url;
  }
}

// Admin Event Submissions API
export const adminEventSubmissionsApi = {
  list: (params?: { status?: string; page?: number; size?: number }) =>
    api.get('/admin/event-submissions', { params }),
  listPending: () => api.get('/admin/event-submissions/pending'),
  getStats: () => api.get('/admin/event-submissions/stats'),
  getDetail: (id: number) => api.get(`/admin/event-submissions/${id}`),
  review: (id: number, action: 'APPROVE' | 'REJECT', adminNotes?: string, internalNotes?: string) =>
    api.post(`/admin/event-submissions/${id}/review`, { action, adminNotes, internalNotes }),
  delete: (id: number) => api.delete(`/admin/event-submissions/${id}`),
};

// Admin Community Profiles API
export const adminCommunityProfilesApi = {
  list: (params?: {
    search?: string;
    newsletterOptIn?: boolean;
    hasAccount?: boolean;
    birthdayMonth?: number;
    page?: number;
    size?: number;
  }) => api.get('/admin/community-profiles', { params }),
  getStats: () => api.get('/admin/community-profiles/stats'),
  getDetail: (id: number) => api.get(`/admin/community-profiles/${id}`),
  delete: (id: number) => api.delete(`/admin/community-profiles/${id}`),
  export: (birthdayMonth?: number) =>
    api.get('/admin/community-profiles/export', { params: birthdayMonth ? { birthdayMonth } : {} }),
};

// Admin Cache API
export const adminCacheApi = {
  clearEvents: () => api.post('/admin/cache/clear/events'),
  clearAll: () => api.post('/admin/cache/clear/all'),
  getStatus: () => api.get('/admin/cache/status'),
};

// Admin Proxy Domains API
export const adminProxyDomainApi = {
  list: () => api.get('/admin/proxy/domains'),
  add: (domain: string) => api.post('/admin/proxy/domains', { domain }),
  remove: (domain: string) => api.delete(`/admin/proxy/domains?domain=${encodeURIComponent(domain)}`),
};
